/**
 * Idempotent, atomic legacy import (spec section 6).
 *
 * prepareImport computes every asynchronous hash (source keys, event ids)
 * up front, because IndexedDB transactions auto-commit across non-Dexie
 * awaits. commitImport then writes all events, their outbox operations,
 * the per-row import markers and the audit batch in ONE transaction: any
 * failure rolls the whole batch back.
 *
 * Identity: source_key = sha256(file id + canonical row + duplicate
 * occurrence index), so legitimately identical rows stay distinct while a
 * reimport maps onto the same keys. event id = uuid(sha256(household + baby +
 * source key)), so the same archive imported into another baby is a separate
 * set of events, and every device derives the same id for the same row.
 */
import { db, META, type ImportBatch, type LocalHousehold } from '@/db/db';
import { ValidationError, assertActiveHousehold, buildEventRecord, enqueueOp, type NewEventInput } from '@/db/repo';
import { fromEventRecord } from '@/lib/schemas';
import { newId, nowIso, sha256Hex, uuidFromHash } from '@/lib/ids';
import {
  LEGACY_SOURCE_SYSTEM,
  LegacyImportError,
  resolveRowTime,
  unresolvedAmbiguities,
  type AmbiguityChoice,
  type FileProblem,
  type LegacyAnalysis,
  type LegacyArchive,
  type ParsedRow,
  type RejectedRow,
} from './legacy';

export interface ImportTarget {
  household_id: string;
  baby_id: string;
}

export interface PreparedRow {
  row: ParsedRow;
  source_key: string;
  event_id: string;
  input: NewEventInput;
}

export interface PreparedImport {
  batch_id: string;
  archiveName: string;
  archiveHash: string;
  target: ImportTarget;
  tz: string;
  rows: PreparedRow[];
  rejected: RejectedRow[];
  /** Ambiguous rows the user chose not to import. */
  userSkipped: number;
  skippedFiles: FileProblem[];
}

export interface ImportResult {
  batch: ImportBatch;
  accepted: number;
  /** Rows already present from an earlier import (including deleted ones). */
  alreadyImported: number;
  userSkipped: number;
  rejected: number;
}

/** Marker kept for every imported row so reimports stay skipped even after local hard deletes. */
export const importMarkerKey = (eventId: string) => `importKey:${eventId}`;
/** Survives household-remapped backups while remaining scoped to the baby. */
export const importSourceMarkerKey = (babyId: string, sourceKey: string) => `importSource:${babyId}:${sourceKey}`;

export function sourceKeyMaterial(fileId: string, canonical: string, occurrence: number): string {
  return `legacy-v1|${fileId}|${canonical}|${occurrence}`;
}

export async function eventIdFor(target: ImportTarget, sourceKey: string): Promise<string> {
  return uuidFromHash(await sha256Hex(`${target.household_id}|${target.baby_id}|${LEGACY_SOURCE_SYSTEM}|${sourceKey}`));
}

function eventInput(row: ParsedRow, baby_id: string, started_ms: number, source_key: string, event_id: string): NewEventInput {
  const source = {
    system: LEGACY_SOURCE_SYSTEM,
    file: row.path.slice(0, 255),
    row: row.row,
    baby: row.sourceBaby,
    raw_time: row.rawTime,
  };
  const common = { id: event_id, baby_id, started_ms, source_system: LEGACY_SOURCE_SYSTEM, source_key };
  switch (row.type) {
    case 'formula':
      return { ...common, type: 'formula', ended_ms: null, payload: { amount_ml: row.amount_ml!, note: row.note, source } };
    case 'sleep':
      return {
        ...common,
        type: 'sleep',
        ended_ms: started_ms + Math.round(row.duration_min! * 60_000),
        payload: { sleep_kind: null, note: row.note, source },
      };
    case 'diaper':
      return { ...common, type: 'diaper', ended_ms: null, payload: { status: row.status!, note: row.note, source } };
  }
}

/**
 * Resolve choices, compute deterministic identities and pre-validate every
 * row. Throws LegacyImportError while any ambiguous time is unresolved.
 */
export async function prepareImport(args: {
  archive: LegacyArchive;
  analysis: LegacyAnalysis;
  choices: Record<string, AmbiguityChoice>;
  target: ImportTarget;
  nowMs?: number;
}): Promise<PreparedImport> {
  const { archive, analysis, choices, target } = args;
  const nowMs = args.nowMs ?? Date.now();
  const unresolved = unresolvedAmbiguities(analysis, choices);
  if (unresolved.length)
    throw new LegacyImportError(`Choose which occurrence to use for ${unresolved.length} ambiguous daylight-saving time(s).`);

  // Occurrence index is assigned over all parsed rows in file order, before
  // user skips, so it does not depend on later choices.
  const seen = new Map<string, number>();
  const keyed = await Promise.all(
    analysis.rows.map(async (row) => {
      const k = `${row.fileId}\u0000${row.canonical}`;
      const occurrence = seen.get(k) ?? 0;
      seen.set(k, occurrence + 1);
      const source_key = await sha256Hex(sourceKeyMaterial(row.fileId, row.canonical, occurrence));
      return { row, source_key, event_id: await eventIdFor(target, source_key) };
    }),
  );

  const rows: PreparedRow[] = [];
  const rejected: RejectedRow[] = [...analysis.rejected];
  let userSkipped = 0;
  for (const k of keyed) {
    const t = resolveRowTime(k.row, choices);
    if (t === 'skip') {
      userSkipped++;
      continue;
    }
    if (t === null) throw new LegacyImportError('Unresolved ambiguous time');
    const input = eventInput(k.row, target.baby_id, t, k.source_key, k.event_id);
    try {
      buildEventRecord(input, target.household_id, null, nowMs);
    } catch (err) {
      const reason = err instanceof ValidationError ? err.message : 'Invalid row';
      const original = archive.files.find((f) => f.fileId === k.row.fileId)?.records.find((r) => r.row === k.row.row);
      rejected.push({ ref: k.row.ref, fileId: k.row.fileId, path: k.row.path, row: k.row.row, type: k.row.type, reason, fields: original?.fields ?? [] });
      continue;
    }
    rows.push({ ...k, input });
  }
  rejected.sort((a, b) => a.path.localeCompare(b.path) || a.row - b.row);

  return {
    batch_id: newId(),
    archiveName: archive.name,
    archiveHash: archive.sha256,
    target,
    tz: analysis.tz,
    rows,
    rejected,
    userSkipped,
    skippedFiles: archive.skipped,
  };
}

/** Whether a row already exists for the target (live, tombstoned, or locally purged). */
async function alreadyImported(p: PreparedRow): Promise<boolean> {
  if (await db.events.get(p.event_id)) return true;
  const bySource = await db.events
    .where('[baby_id+source_system+source_key]')
    .equals([p.input.baby_id, LEGACY_SOURCE_SYSTEM, p.source_key])
    .first();
  if (bySource) return true;
  const marker = await db.meta.get(importMarkerKey(p.event_id));
  if (marker) return true;
  if (await db.meta.get(importSourceMarkerKey(p.input.baby_id, p.source_key))) return true;
  return !!(await db.meta.get(`undoEvent:${p.event_id}`));
}

/** Read-only count of prepared rows that a commit would skip as already imported. */
export async function countAlreadyImported(prepared: PreparedImport): Promise<number> {
  return db.transaction('r', [db.events, db.meta], async () => {
    let n = 0;
    for (const p of prepared.rows) if (await alreadyImported(p)) n++;
    return n;
  });
}

/**
 * Commit a prepared import atomically. Revalidates household and baby
 * ownership inside the transaction; concurrent commits of the same archive
 * are serialized by IndexedDB, so the later one skips every row.
 */
export async function commitImport(prepared: PreparedImport, nowMs: number = Date.now()): Promise<ImportResult> {
  const startedAt = nowIso(nowMs);
  return db.transaction('rw', [db.events, db.babies, db.outbox, db.meta, db.importBatches], async () => {
    const household = (await db.meta.get(META.household))?.value as LocalHousehold | undefined;
    if (!household || household.id !== prepared.target.household_id)
      throw new LegacyImportError('The active household changed. Review the import again before confirming.');
    await assertActiveHousehold(prepared.target.household_id, true);
    const baby = await db.babies.get(prepared.target.baby_id);
    if (!baby || baby.deleted_at || baby.household_id !== household.id)
      throw new LegacyImportError('The selected baby is no longer available in this household.');
    if (baby.timezone !== prepared.tz)
      throw new LegacyImportError('The baby timezone changed. Review the import again before confirming.');
    if (await db.importBatches.get(prepared.batch_id)) throw new LegacyImportError('This import has already been committed.');
    const actor = ((await db.meta.get(META.session))?.value as { userId?: string } | undefined)?.userId ?? null;

    let accepted = 0;
    let alreadyCount = 0;
    for (const p of prepared.rows) {
      if (await alreadyImported(p)) {
        alreadyCount++;
        continue;
      }
      const rec = buildEventRecord(p.input, household.id, actor, nowMs);
      await db.events.add(fromEventRecord(rec, 'local'));
      await enqueueOp({
        household_id: household.id,
        entity: 'event',
        entity_id: rec.id,
        op: 'create',
        data: rec,
        currentServerVersion: 0,
      });
      const marker = {
        batch_id: prepared.batch_id, baby_id: baby.id, household_id: household.id,
        source_system: LEGACY_SOURCE_SYSTEM, source_key: p.source_key,
      };
      await db.meta.put({ key: importMarkerKey(rec.id), value: marker });
      await db.meta.put({ key: importSourceMarkerKey(baby.id, p.source_key), value: marker });
      accepted++;
    }

    const batch: ImportBatch = {
      id: prepared.batch_id,
      household_id: household.id,
      baby_id: baby.id,
      filename: prepared.archiveName.slice(0, 255),
      file_hash: prepared.archiveHash,
      started_at: startedAt,
      finished_at: nowIso(),
      accepted,
      skipped: alreadyCount + prepared.userSkipped,
      rejected: prepared.rejected.length,
    };
    await db.importBatches.add(batch);
    return { batch, accepted, alreadyImported: alreadyCount, userSkipped: prepared.userSkipped, rejected: prepared.rejected.length };
  });
}
